import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MessageSquare, User, Calendar, Send, CheckCircle2 } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getIssue, addSolution, updateIssueStatus } from '../api';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const getMediaSrc = (uploadId, filename) => {
  return `${BACKEND_URL}/api/media/${uploadId}/${encodeURIComponent(filename)}`;
};

const IssueDetailPage = () => {
  const { issueId } = useParams();
  const navigate = useNavigate();
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newSolution, setNewSolution] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadIssue();
  }, [issueId]);

  const loadIssue = async () => {
    setLoading(true);
    try {
      const data = await getIssue(issueId);
      setIssue(data);
    } catch (err) {
      toast.error('Failed to load issue');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSolution = async () => {
    if (!newSolution.trim()) return;

    setSubmitting(true);
    try {
      await addSolution(issueId, {
        text: newSolution,
        confirmed: true,
      });
      toast.success('Solution added!');
      setNewSolution('');
      loadIssue();
    } catch (err) {
      toast.error('Failed to add solution');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      await updateIssueStatus(issueId, { status });
      toast.success('Status updated');
      loadIssue();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handlePriorityChange = async (priority) => {
    try {
      await updateIssueStatus(issueId, { status: issue.status, priority });
      toast.success('Priority updated');
      loadIssue();
    } catch (err) {
      toast.error('Failed to update priority');
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: 'bg-[hsl(0_84%_96%)] text-[hsl(0_72%_35%)] border-[hsl(0_70%_85%)]',
      high: 'bg-[hsl(43_100%_95%)] text-[hsl(30_90%_30%)] border-[hsl(43_90%_85%)]',
      medium: 'bg-[hsl(199_70%_95%)] text-[hsl(199_89%_28%)] border-[hsl(199_60%_85%)]',
      low: 'bg-[hsl(210_20%_96%)] text-[hsl(215_16%_40%)] border-[hsl(214_20%_88%)]',
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-[hsl(199_70%_95%)] text-[hsl(199_89%_28%)]',
      triaged: 'bg-[hsl(43_100%_95%)] text-[hsl(30_90%_30%)]',
      in_progress: 'bg-[hsl(186_45%_92%)] text-[hsl(199_89%_20%)]',
      resolved: 'bg-[hsl(150_45%_93%)] text-[hsl(160_60%_28%)]',
      wont_fix: 'bg-[hsl(210_18%_94%)] text-[hsl(215_16%_40%)]',
    };
    return colors[status] || colors.open;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!issue) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Issue not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h1 className="font-display text-3xl font-bold mb-2" data-testid="issue-detail-title">
                {issue.title}
              </h1>
              <p className="text-muted-foreground">{issue.summary}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-display text-4xl font-bold text-primary tabular-nums">
                {issue.count}
              </span>
              <span className="text-sm text-muted-foreground">occurrences</span>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <Select value={issue.status} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-[150px]" data-testid="issue-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="triaged">Triaged</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="wont_fix">Won't Fix</SelectItem>
              </SelectContent>
            </Select>
            <Select value={issue.priority} onValueChange={handlePriorityChange}>
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex flex-wrap gap-2">
              {issue.tags?.map((tag) => (
                <Badge key={tag} variant="secondary">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6">
          {/* Main Content */}
          <div className="space-y-6">
            {/* Timeline */}
            <Card>
              <CardHeader>
                <CardTitle>Occurrence Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                {issue.timeline && issue.timeline.length > 1 ? (
                  <ResponsiveContainer width="100%" height={200} data-testid="issue-detail-timeline-chart">
                    <AreaChart data={issue.timeline}>
                      <defs>
                        <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis
                        dataKey="date"
                        stroke="hsl(var(--muted-foreground))"
                        style={{ fontSize: '12px' }}
                      />
                      <YAxis
                        stroke="hsl(var(--muted-foreground))"
                        style={{ fontSize: '12px' }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                          fontSize: '12px',
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="count"
                        stroke="hsl(var(--chart-1))"
                        fillOpacity={1}
                        fill="url(#colorCount)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Not enough data points for timeline visualization
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Evidence Messages */}
            <Card>
              <CardHeader>
                <CardTitle>Evidence Messages</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] pr-4" data-testid="issue-detail-evidence-list">
                  <div className="space-y-3">
                    {issue.evidence_messages?.length > 0 ? (
                      issue.evidence_messages.map((msg) => (
                        <div
                          key={msg.id}
                          className="rounded-lg border bg-background px-3 py-2"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <User className="w-3 h-3 text-muted-foreground" />
                            <span className="text-sm font-medium">{msg.sender}</span>
                            <span className="text-xs text-muted-foreground font-mono">
                              {new Date(msg.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm leading-relaxed">{msg.text}</p>
                          {msg.media_file && (
                            <div className="mt-2">
                              {msg.media_file.match(/\.(jpg|jpeg|png)$/i) ? (
                                <img
                                  src={getMediaSrc(issue.upload_id, msg.media_file)}
                                  alt="WhatsApp attachment"
                                  className="max-w-full h-auto rounded-lg border"
                                  style={{ maxHeight: '300px' }}
                                  loading="lazy"
                                />
                              ) : msg.media_file.match(/\.(mp4|avi|mov)$/i) ? (
                                <video
                                  controls
                                  className="max-w-full h-auto rounded-lg border"
                                  style={{ maxHeight: '300px' }}
                                >
                                  <source
                                    src={getMediaSrc(issue.upload_id, msg.media_file)}
                                    type="video/mp4"
                                  />
                                  Your browser does not support the video tag.
                                </video>
                              ) : (
                                <a
                                  href={getMediaSrc(issue.upload_id, msg.media_file)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-primary hover:underline"
                                >
                                  📎 {msg.media_file}
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No evidence messages found
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Solutions Panel */}
          <div className="space-y-6">
            {/* Add Solution */}
            <Card>
              <CardHeader>
                <CardTitle>Add Solution</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Textarea
                  placeholder="Describe the solution..."
                  value={newSolution}
                  onChange={(e) => setNewSolution(e.target.value)}
                  rows={4}
                  data-testid="solution-add-textarea"
                />
                <Button
                  onClick={handleAddSolution}
                  disabled={!newSolution.trim() || submitting}
                  className="w-full"
                  data-testid="solution-add-submit-button"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Add Solution
                </Button>
              </CardContent>
            </Card>

            {/* Existing Solutions */}
            <Card>
              <CardHeader>
                <CardTitle>Solutions ({issue.solutions?.length || 0})</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-3">
                    {issue.solutions?.length > 0 ? (
                      issue.solutions.map((solution) => (
                        <div
                          key={solution.id}
                          className="rounded-lg border bg-background p-3"
                          data-testid="solution-item"
                        >
                          <div className="flex items-start gap-2 mb-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm leading-relaxed">{solution.text}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono">
                            <Calendar className="w-3 h-3" />
                            {new Date(solution.created_at).toLocaleDateString()}
                            {solution.confirmed && (
                              <Badge variant="secondary" className="ml-auto text-xs">
                                Confirmed
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No solutions yet. Be the first to add one!
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Metadata */}
            <Card>
              <CardHeader>
                <CardTitle>Metadata</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">First Seen</span>
                  <span className="font-mono">
                    {new Date(issue.first_seen).toLocaleDateString()}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Seen</span>
                  <span className="font-mono">
                    {new Date(issue.last_seen).toLocaleDateString()}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Reports</span>
                  <span className="font-display font-semibold tabular-nums">
                    {issue.count}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IssueDetailPage;